import { TIMEZONE_DEFAULT, TIMEZONE_OPTIONS } from './time.js';
import { THEME_CHOICES, THEME_CHOICE_DEFAULT } from './theme.js';
/**
 * The one literal home for the product's default name (design doc §11/§8).
 * `b.name` and `b.binary` below both point at this single constant instead
 * of each holding the literal independently — two places writing the same
 * string is exactly how they drift.
 *
 * Also read directly, as plain text (not imported), by
 * `deploy/runtime-config/fill-from-env.mjs` to fill a deployment's
 * `client.json` at deploy time. Deliberately NOT imported by
 * `apps/client/src/runtime-config.ts` for that same purpose: a bundler that
 * *uses* this string value compiles it into the shipped client bundle
 * regardless of which binding it came in through, which is precisely the
 * "hardcoded in a source file" outcome this config surface exists to avoid
 * (see that file's own comment, and the Task 37 report, for the attempt that
 * tripped `deploy/test/no-literals.test.mjs` over this).
 */
/**
 * What this product calls itself to a human — the default for `b.name`, and
 * what the deploy templating writes into the client's runtime config.
 *
 * TOSSKER, not Communicator. The repo, the packages, the containers and the
 * code all stay `communicator` on purpose; this is the only one of those that
 * a CUSTOMER ever reads, and it had been left behind by the rename. `b.name`
 * has said Tossker for days — its own hint claims it governs "Console, client
 * app, all copy" — while the client app went on introducing itself as
 * Communicator, because its name is filled from THIS constant at deploy time
 * and not from that setting.
 */
export const PRODUCT_NAME_DEFAULT = 'Tossker';
/**
 * Names this product used to answer to.
 *
 * The deploy templating fills `productName` into the client's runtime config
 * ONCE, and thereafter leaves it alone — which is correct for a value an
 * operator chose, and wrong for one this system wrote itself and then
 * renamed. Listing the superseded names is what lets the templating tell
 * those two apart and re-point its own stale value, instead of a customer
 * being greeted by a name the company stopped using days ago.
 *
 * Append here on a rename; never remove an entry, or a deployment that has
 * been sitting on that old value stops being repaired.
 */
export const PRODUCT_NAME_SUPERSEDED = ['Communicator'];
/**
 * The origin a Tauri v2 webview reports on macOS. A fact about Tauri, not a
 * deployment value — confirmed in Computer-Cats/desktop-chat from Tauri's own
 * source and real build logs rather than guessed.
 */
export const TAURI_ORIGIN_MACOS = 'tauri://localhost';
/**
 * The origin a Tauri v2 webview reports on Windows.
 *
 * Unlike `tauri://`, which no web page can ever hold, `tauri.localhost`
 * resolves to loopback under RFC 6761 — so a page served by any local
 * listener is a genuine browser origin that this allowlist admits.
 *
 * What bounds that, for this CLIENT: its credentials are not ambient. It
 * sends `authorization: Bearer <sessionToken>` (apps/client/src/lib/
 * transport.ts) with the token read from localStorage (apps/client/src/lib/
 * auth.ts), and no cookie is involved, so nothing is attached by the browser
 * automatically. A hostile page at this origin gets its own storage
 * partition, holds no session token, and can make an admitted request
 * carrying nothing. For the client, the entry widens which origins may call;
 * it does not widen what one can prove.
 *
 * That is NOT the whole picture, though, because this origin is also
 * admitted into `services/auth`'s allowlist, and `services/auth` does put a
 * session token in a cookie — `session_token` and `refresh_token`, set on
 * staff sign-in (services/auth/src/app.ts), read back by /auth/refresh and
 * /auth/signout — and registers CORS there with `credentials: true`. So a
 * page at this origin genuinely can issue a credentialed cross-site request
 * to those routes. What stops the browser from attaching the cookie to it is
 * `sameSite: 'lax'` on that cookie (same file), not the cookie's absence:
 * Lax is not sent on a cross-site fetch/XHR, and `tauri.localhost` is not
 * same-site with the deployment host. The entry is safe as shipped, but on a
 * narrower foundation than the client side alone suggests.
 *
 * This origin is also admitted into `services/relay`'s allowlist
 * (services/relay/src/app.ts), the third and last of this constant's three
 * consumers, and relay ALSO registers CORS with `credentials: true` — same
 * shape of exposure as auth, at first glance. But relay's own auth is not
 * ambient either: its routes are verified by a `?token=` query parameter or
 * a Bearer header (services/relay/src/verify.ts and the route handlers), not
 * by a cookie the browser would attach on its own. `credentials: true` only
 * governs whether the browser is ALLOWED to send cookies/credentials cross-
 * site; it does not manufacture a credential relay never asked for. A
 * hostile page at this origin can reach relay's admitted routes, same as it
 * can reach auth's, but it still holds no session token to send with the
 * request. The bound holds here for the same reason it holds for the client
 * itself, not for the reason it holds for auth.
 *
 * REVISIT THIS if ANY of the following changes — and note these two are not
 * equally enforced: the first is pinned by a test and breaks loudly; the
 * second is pinned by nothing and would land silently.
 *   - `services/auth`'s cookies weakening from `sameSite: 'lax'` to
 *     `sameSite: 'none'`. PINNED: `services/auth/test/staff.test.ts` asserts
 *     `sameSite === 'Lax'` on both cookies, so this regressing fails a test.
 *   - The client's session token moving from `localStorage` into a cookie
 *     (which would also make relay's and auth's exposure identical in kind,
 *     not just in shape). NOT PINNED by any test — this could regress and
 *     nothing here would catch it.
 */
export const TAURI_ORIGIN_WINDOWS = 'http://tauri.localhost';
/**
 * Every deployment- and brand-specific value. Nothing else may hold a literal.
 * Transcribed verbatim from `docs/design-handoff/data/settings-model.json`.
 */
export const CONFIG_FIELDS = [
    // section: personal — Personal preferences (PER-USER, gate: personal)
    {
        id: 'p.sound',
        section: 'personal',
        kind: 'toggle',
        label: 'Notification sound',
        hint: 'On new comms you are notified about',
        tier: 'per-user',
        gate: 'personal',
        default: true,
    },
    {
        id: 'p.desktop',
        section: 'personal',
        kind: 'toggle',
        label: 'Desktop notifications',
        hint: 'Scoped by role plus ownership',
        tier: 'per-user',
        gate: 'personal',
        default: true,
    },
    {
        id: 'p.push',
        section: 'personal',
        kind: 'toggle',
        label: 'Push to my phone',
        // Defaults ON, and absent counts as on server-side too: a tech who has
        // never opened Settings should still be reachable, because the failure
        // mode of the alternative is a team that believes it is covered and is not.
        hint: 'Pages your registered devices when a client writes on a thread you own, or on any unowned thread while you are covering. Never carries the message text.',
        tier: 'per-user',
        gate: 'personal',
        default: true,
    },
    {
        id: 'p.preview',
        section: 'personal',
        kind: 'toggle',
        label: 'Show message text in notifications',
        // ON by default, and the trade is worth stating: a page you cannot triage
        // without opening the app is half useless, which is why SMS and Slack both
        // preview. The cost is that a customer's words render on a lock screen and
        // pass through Apple's and Google's servers, and conversations here are in
        // HIPAA scope. Per person rather than per tenant, because it is their lock
        // screen; make it admin-gated if it ever needs enforcing org-wide.
        hint: 'A few words of the message, so you can tell whether it needs you now. Off sends only who it is from — conversations here are in HIPAA scope and this renders on a lock screen.',
        tier: 'per-user',
        gate: 'personal',
        default: true,
    },
    {
        id: 'p.ctx',
        section: 'personal',
        kind: 'toggle',
        label: 'Context pane open by default',
        hint: 'Remembered per user',
        tier: 'per-user',
        gate: 'personal',
        default: true,
    },
    {
        id: 'p.theme',
        section: 'personal',
        kind: 'select',
        label: 'Appearance',
        // Names the two families in the hint rather than the label, because the
        // option strings already carry them and a label that explained itself
        // would be longer than the control.
        hint: 'Tossker is the new look. Legacy keeps the colours this console shipped with. Auto follows your device between light and dark.',
        tier: 'per-user',
        gate: 'personal',
        options: [...THEME_CHOICES],
        default: THEME_CHOICE_DEFAULT,
    },
    {
        id: 'p.view',
        section: 'personal',
        kind: 'text',
        label: 'Default view',
        hint: 'Where the rail lands when you sign in',
        tier: 'per-user',
        gate: 'personal',
        default: 'Queue',
    },
    {
        id: 'p.tz',
        section: 'personal',
        kind: 'select',
        label: 'Timezone',
        // 2026-08-24 design ("the messages are all showing in UTC, which makes
        // no sense to humans"): the viewer's own zone for every human-facing
        // message timestamp in the console (apps/console/src/components/thread/
        // format.ts's formatMessageStamp). Distinct from `v.tz` above — that one
        // is org-wide and governs what gets WRITTEN into a Halo note; this one is
        // per-user and only ever changes what one person SEES on screen.
        hint: 'Message timestamps show in this zone',
        tier: 'per-user',
        gate: 'personal',
        // 'auto' resolves to this device's own timezone
        // (Intl.DateTimeFormat().resolvedOptions().timeZone) so a fresh account
        // is correct without ever opening Settings — see formatMessageStamp.
        options: [
            'auto',
            'America/New_York',
            'America/Chicago',
            'America/Denver',
            'America/Los_Angeles',
            'America/Phoenix',
            'America/Anchorage',
            'Pacific/Honolulu',
            'UTC',
        ],
        default: 'auto',
    },
    // section: brand — Naming and brand (RUNTIME-HOT, gate: admin)
    {
        id: 'b.logo',
        section: 'brand',
        kind: 'logo',
        label: 'Logo',
        hint: 'Two files: one that reads on the dark UI, one for light. SVG or PNG with transparency.',
        tier: 'runtime-hot',
        gate: 'admin',
        default: null,
    },
    {
        id: 'b.name',
        section: 'brand',
        kind: 'text',
        label: 'Product name',
        hint: 'Console, client app, all copy',
        tier: 'runtime-hot',
        gate: 'admin',
        default: PRODUCT_NAME_DEFAULT,
    },
    {
        id: 'b.resolveLabel',
        section: 'brand',
        kind: 'text',
        label: 'Resolve button',
        hint: "Wording of the thread's primary resolve action, e.g. “Resolve” or “Conversation ended”",
        tier: 'runtime-hot',
        gate: 'admin',
        default: 'Resolve',
    },
    {
        id: 'b.host',
        section: 'brand',
        kind: 'text',
        label: 'Console host',
        hint: 'Team surface. Also drives the CORS allowlist.',
        tier: 'runtime-hot',
        gate: 'admin',
        default: 'communicator.example.net',
    },
    {
        id: 'b.path',
        section: 'brand',
        kind: 'text',
        label: 'Client app path',
        hint: 'Path under the same host, not a chat-named domain',
        tier: 'runtime-hot',
        gate: 'admin',
        default: '/client',
    },
    {
        id: 'b.binary',
        section: 'brand',
        kind: 'static',
        label: 'Name in the client binary',
        hint: 'Build-time. A rename here is a rebuild and repush.',
        tier: 'runtime-hot',
        gate: 'admin',
        default: PRODUCT_NAME_DEFAULT,
    },
    // section: channels — Channels and SLA (RUNTIME-HOT, gate: admin)
    {
        id: 'c.chat',
        section: 'channels',
        kind: 'number',
        unit: 'min',
        label: 'Chat response target',
        hint: 'Relay transport · we own the endpoint',
        tier: 'runtime-hot',
        gate: 'admin',
        default: 5,
    },
    {
        id: 'c.sms',
        section: 'channels',
        kind: 'number',
        unit: 'min',
        label: 'SMS response target',
        hint: 'Twilio · carrier handles delivery and queuing',
        tier: 'runtime-hot',
        gate: 'admin',
        default: 20,
    },
    {
        id: 's.from',
        section: 'channels',
        kind: 'text',
        label: 'SMS sending number',
        hint: 'E.164, e.g. +17195550100. LAST-RESORT fallback only: once any line exists in Settings → Lines, the lines table is the authority and this value is ignored. Kept for lines-less deployments and the transition.',
        tier: 'runtime-hot',
        gate: 'admin',
        default: '',
    },
    {
        id: 'c.voice',
        section: 'channels',
        kind: 'number',
        unit: 'min',
        label: 'Voice response target',
        hint: '3CX · a live call still sits in the hard top band regardless of this number',
        tier: 'runtime-hot',
        gate: 'admin',
        default: 1,
    },
    {
        id: 'c.inboundRateWindowS',
        section: 'channels',
        kind: 'number',
        unit: 's',
        label: 'Inbound rate-limit window',
        hint: 'Per-contact flood control on the client chat message route — a sliding window, not a hard cutoff',
        tier: 'runtime-hot',
        gate: 'admin',
        default: 60,
    },
    {
        id: 'c.inboundRateMax',
        section: 'channels',
        kind: 'number',
        label: 'Inbound rate-limit max',
        hint: 'Max messages one contact may send within the window above before the client route refuses with a 429',
        tier: 'runtime-hot',
        gate: 'admin',
        default: 20,
    },
    // section: halo — Halo integration (RUNTIME-HOT, gate: admin)
    {
        id: 'h.endpoint',
        section: 'halo',
        kind: 'text',
        label: 'Halo endpoint',
        hint: 'Resolved from config at runtime. Credentials live separately and are never shown.',
        tier: 'runtime-hot',
        gate: 'admin',
        default: 'https://halo.example.com/api',
    },
    {
        id: 'h.appid',
        section: 'halo',
        kind: 'text',
        label: 'Application (client) ID',
        hint: 'From the Halo API application. Not a secret.',
        tier: 'runtime-hot',
        gate: 'admin',
        default: '8f2c41d0-communicator',
    },
    {
        id: 'h.secret',
        section: 'halo',
        kind: 'secret',
        label: 'Client secret',
        hint: "Write-only. Stored in the deployment's secret store, never rendered back.",
        tier: 'runtime-hot',
        gate: 'admin',
        default: 'set · 12 Jul 2026',
    },
    {
        id: 'h.tenant',
        section: 'halo',
        kind: 'text',
        label: 'Halo tenant',
        hint: 'Blank for a single-tenant Halo instance',
        tier: 'runtime-hot',
        gate: 'admin',
        default: '',
    },
    {
        id: 'h.chargerate',
        section: 'halo',
        kind: 'number',
        label: 'Time entry charge rate',
        hint: 'Halo charge rate id stamped on every time entry Tossker writes (lookup 17; 20 = MS Communications). 0 leaves Halo to resolve it, which on a Note is No Charge.',
        tier: 'runtime-hot',
        gate: 'admin',
        default: 20,
    },
    {
        id: 'h.quickfix',
        section: 'halo',
        kind: 'number',
        label: 'Quick Fix ticket type',
        hint: 'Halo tickettype_id used when a call is dispositioned as a Quick Fix',
        tier: 'runtime-hot',
        gate: 'admin',
        default: 56,
    },
    {
        id: 'h.rollup',
        section: 'halo',
        kind: 'toggle',
        label: 'Hourly rollup',
        hint: 'Pushes the delta if non-empty',
        tier: 'runtime-hot',
        gate: 'admin',
        default: true,
    },
    {
        id: 'h.close',
        section: 'halo',
        kind: 'toggle',
        label: 'Write on thread close',
        hint: 'Final remainder plus close note',
        tier: 'runtime-hot',
        gate: 'admin',
        default: true,
    },
    {
        id: 'h.watcher',
        section: 'halo',
        kind: 'toggle',
        label: 'Ticket-closed watcher',
        hint: 'Closing in Halo closes every linked thread, zero clicks',
        tier: 'runtime-hot',
        gate: 'admin',
        default: true,
    },
    {
        id: 'h.draft',
        section: 'halo',
        kind: 'toggle',
        label: 'Close-note draft',
        hint: 'Pre-filled from thread history, editable',
        tier: 'runtime-hot',
        gate: 'admin',
        default: true,
    },
    // section: identity — Identity and trust (RUNTIME-HOT, gate: admin)
    {
        id: 'i.otp',
        section: 'identity',
        kind: 'select',
        options: ['SMS + email', 'SMS only', 'Email only'],
        label: 'OTP delivery',
        hint: 'Always to the destination of record on the Halo contact, never to a handle the requester supplies',
        tier: 'runtime-hot',
        gate: 'admin',
        default: 'SMS + email',
    },
    {
        id: 'i.ceiling',
        section: 'identity',
        kind: 'select',
        options: ['Matched', 'OTP-Verified'],
        label: 'Handle ceiling',
        hint: 'The highest tier a bare handle can reach. Above this needs OTP; Verified needs a signed device.',
        tier: 'runtime-hot',
        gate: 'admin',
        default: 'Matched',
    },
    {
        id: 'i.alias',
        section: 'identity',
        kind: 'toggle',
        label: 'Alias table',
        hint: 'Pressure valve for messy handles, not the default',
        tier: 'runtime-hot',
        gate: 'admin',
        default: true,
    },
    {
        id: 'i.risk',
        section: 'identity',
        kind: 'select',
        options: ['Undecided', 'OTP-Verified or above', 'Verified only (crypto)'],
        label: 'Highest-risk actions',
        hint: 'Whether the riskiest actions accept OTP or demand a signed device. Still an open decision.',
        tier: 'runtime-hot',
        gate: 'admin',
        default: 'Undecided',
    },
    // section: auth — Enrolment and auth (BUILD + RUNTIME, gate: admin)
    {
        id: 'a.secret',
        section: 'auth',
        kind: 'static',
        label: 'Deployment secret',
        hint: 'Client-scoped, baked into the RMM package',
        tier: 'build-time',
        gate: 'admin',
        default: 'set · value not shown',
    },
    {
        id: 'a.m365',
        section: 'auth',
        kind: 'static',
        label: 'M365 clients',
        hint: 'System browser or Windows broker, never a webview',
        tier: 'build-time',
        gate: 'admin',
        default: 'Sign in with Microsoft',
    },
    {
        id: 'a.other',
        section: 'auth',
        kind: 'static',
        label: 'Everyone else',
        hint: 'In-app',
        tier: 'build-time',
        gate: 'admin',
        default: 'email + OTP',
    },
    {
        id: 'a.personal',
        section: 'auth',
        kind: 'number',
        unit: 'days',
        label: 'Personal device refresh',
        // SLIDING, and that is the thing to know. Every rotation sets the expiry
        // to now + this many days, with no reference to when the session began —
        // so somebody using the console regularly is never asked to sign in again.
        //
        // 60 → 7 → 60. Andrew asked for 7 in August after seeing his own session
        // had not lapsed in months; on 2026-09-11 he reversed it deliberately
        // ("make it work like Slack and still be secure"): the security control
        // for a stolen device is rotation + replay-revocation + server-side
        // revocation, not expiry — expiry mostly punished his own phone. 60 days
        // of DORMANCY signs you out; any regular user never sees the screen.
        // There is deliberately still no absolute cap; the coming signed-in
        // devices view is the review-and-revoke half of this bargain.
        hint: 'Rotating, device-bound, and SLIDING: each use resets the clock, so regular users stay signed in. Reuse trips an anomaly you can revoke on.',
        tier: 'build-time',
        gate: 'admin',
        default: 60,
    },
    {
        id: 'a.shared',
        section: 'auth',
        kind: 'static',
        label: 'Shared device session',
        hint: 'Applies to M365 users on a conference PC too',
        tier: 'build-time',
        gate: 'admin',
        default: 'single-use',
    },
    // section: entra — Staff sign-in with Entra (INFRA-TIME, gate: admin)
    //
    // v2.3 §5: "Team members sign in with Entra against the operating MSP's own
    // tenant, plain SSO." These are the app registration's own coordinates.
    // Their live VALUES are read from this deployment's env by services/auth
    // (ENTRA_TENANT_ID, ENTRA_CLIENT_ID, ENTRA_REDIRECT_URI,
    // ENTRA_CLIENT_SECRET, ENTRA_AUTHORITY) — the fields here exist so an
    // admin can see what is configured, and so the secret can be replaced,
    // exactly as `h.secret` works for Halo. Blank defaults mean "Entra is not
    // configured", which is a supported state: staff then sign in with email
    // plus a one-time code, the same OTP primitive the client side uses.
    {
        id: 'e.tenant',
        section: 'entra',
        kind: 'text',
        label: 'Directory (tenant) ID',
        hint: "The operating MSP's own tenant. Sign-ins are accepted from this tenant and no other.",
        tier: 'infra-time',
        gate: 'admin',
        default: '',
        envVar: 'ENTRA_TENANT_ID',
    },
    {
        id: 'e.client',
        section: 'entra',
        kind: 'text',
        label: 'Application (client) ID',
        hint: 'From the app registration. Not a secret.',
        tier: 'infra-time',
        gate: 'admin',
        default: '',
        envVar: 'ENTRA_CLIENT_ID',
    },
    {
        id: 'e.redirect',
        section: 'entra',
        kind: 'text',
        label: 'Redirect URI',
        hint: 'Must match the app registration exactly. Its path is also the callback this deployment serves.',
        tier: 'infra-time',
        gate: 'admin',
        default: '',
        envVar: 'ENTRA_REDIRECT_URI',
    },
    {
        id: 'e.secret',
        section: 'entra',
        kind: 'secret',
        label: 'Client secret',
        hint: "Write-only. Stored in the deployment's secret store, never rendered back.",
        tier: 'infra-time',
        gate: 'admin',
        default: '',
        envVar: 'ENTRA_CLIENT_SECRET',
    },
    {
        id: 'e.authority',
        section: 'entra',
        kind: 'text',
        label: 'Identity provider authority',
        hint: 'Only change this for a sovereign or national cloud. Endpoints are discovered from it, never assumed.',
        tier: 'infra-time',
        gate: 'admin',
        default: 'https://login.microsoftonline.com',
        envVar: 'ENTRA_AUTHORITY',
    },
    // section: coverage — Roles and coverage (RUNTIME-HOT, gate: admin)
    {
        id: 'v.pool',
        section: 'coverage',
        kind: 'static',
        label: 'Coverage pool',
        hint: 'Anyone flipped to dispatch counts',
        tier: 'runtime-hot',
        gate: 'admin',
        default: '1 of 4 covering',
    },
    {
        id: 'v.defaultRole',
        section: 'coverage',
        kind: 'select',
        options: ['dispatch', 'tech'],
        label: 'Default role for new staff',
        // Users-and-permissions design §5: role stops being self-service
        // (POST /ops/coverage now refuses it), so a brand-new hire's starting
        // role has to come from somewhere — this is that somewhere. Read fresh
        // at provisioning time (services/auth/src/staff.ts), not captured once
        // at boot, so an admin changing it takes effect on the very next
        // sign-in rather than the next deploy. What it never touches: the
        // FIRST staff row of all, which provisions as SuperAdmin regardless of
        // this setting (design §5) — that row's authority comes from being
        // first, not from this default.
        hint: 'What a newly auto-provisioned staff member starts as. An admin still assigns the real role afterward from the Users page.',
        tier: 'runtime-hot',
        gate: 'admin',
        default: 'tech',
    },
    {
        id: 'v.hours',
        section: 'coverage',
        kind: 'hours',
        label: 'Business hours',
        hint: 'Per day. Outside these hours the queue behaves exactly as it does when nobody is covering.',
        tier: 'runtime-hot',
        gate: 'admin',
        default: {
            mon: { on: true, from: '08:00', to: '17:30' },
            tue: { on: true, from: '10:00', to: '17:30' },
            wed: { on: true, from: '08:00', to: '17:30' },
            thu: { on: true, from: '08:00', to: '17:30' },
            fri: { on: true, from: '08:00', to: '17:00' },
            sat: { on: false },
            sun: { on: false },
        },
    },
    {
        id: 'v.tz',
        section: 'coverage',
        kind: 'select',
        label: 'Timezone',
        hint: 'IANA zone. Every timestamp shown to staff, and every one written into a Halo note, is rendered in this zone — so a note read months later says when something happened in our time, not the server’s.',
        tier: 'runtime-hot',
        gate: 'admin',
        options: TIMEZONE_OPTIONS,
        default: TIMEZONE_DEFAULT,
    },
    {
        id: 'v.msg',
        section: 'coverage',
        kind: 'text',
        label: 'After-hours message',
        hint: 'Client-facing copy, runtime-hot',
        tier: 'runtime-hot',
        gate: 'admin',
        // Matches apps/client/src/screens/Conversation.tsx's own fallback
        // literal exactly (fix round 2, final review) — an admin who has never
        // touched this field and the client's own hard-coded fallback must say
        // the same thing, not two different "nobody's here" sentences.
        default: "Nobody is online right now — leave a message and we'll pick it up when we're back.",
    },
    {
        id: 'v.peek',
        section: 'coverage',
        kind: 'toggle',
        label: 'Tech peek',
        hint: 'Read others without their alerts',
        tier: 'runtime-hot',
        gate: 'admin',
        default: true,
    },
    {
        id: 'v.timeout',
        // The design/plan docs for this feature call this "the voice section",
        // but there is no standalone `voice` section in this codebase — every
        // other `v.*` field lives under `coverage`, and the console's
        // `SECTION_ORDER` (apps/console/src/components/settings/sections.ts) is a
        // fixed, tested list of exactly the sections that actually exist. A field
        // whose `section` names one that isn't in that list is filtered out of
        // `buildSections()` silently — no test catches it — and never renders on
        // the Settings page at all. Filed under `coverage`, matching every
        // sibling `v.*` id, so it actually shows up.
        section: 'coverage',
        kind: 'number',
        label: 'Auto-close quiet time (seconds)',
        hint: 'An ended inbound call with no write-up closes itself — and bills — after this much quiet. 0 disables the sweep.',
        tier: 'runtime-hot',
        gate: 'admin',
        default: 300,
    },
    {
        id: 'v.outboundClose',
        section: 'coverage',
        kind: 'toggle',
        label: 'Auto-close idle outbound calls',
        hint: 'An ended outbound call nobody claimed closes itself after the quiet time below. No billing — outbound time is captured in Halo. Off leaves them for you to close by hand.',
        tier: 'runtime-hot',
        gate: 'admin',
        default: true,
    },
    {
        id: 'v.outboundTimeout',
        section: 'coverage',
        kind: 'number',
        label: 'Outbound auto-close quiet time (seconds)',
        hint: 'How much quiet before an unclaimed outbound call closes. 0 also disables the sweep.',
        tier: 'runtime-hot',
        gate: 'admin',
        default: 120,
    },
    // section: retention — Transcripts and retention (OPEN DECISION, gate: admin)
    {
        id: 'r.store',
        section: 'retention',
        kind: 'select',
        options: ['Thread store', 'Thread store + encrypted archive', 'Halo only'],
        label: 'Where the verbatim lives',
        hint: 'Halo holds summaries and deltas. The verbatim is in scope for HIPAA retention and access wherever it sits.',
        tier: 'open-decision',
        gate: 'admin',
        default: 'Thread store',
    },
    {
        id: 'r.window',
        section: 'retention',
        kind: 'select',
        options: ['Undecided', '90 days', '1 year', '3 years', '7 years', 'Indefinite'],
        label: 'Retention window',
        hint: 'Open decision. Clients paste things into a chat box they would never put in a ticket.',
        tier: 'open-decision',
        gate: 'admin',
        default: 'Undecided',
    },
    {
        id: 'r.access',
        section: 'retention',
        kind: 'select',
        options: ['Dispatch and admin', 'Admin only', 'Anyone on the team'],
        label: 'Who can read a closed transcript',
        hint: 'Read access on live threads is open across the team; closed transcripts are a separate question.',
        tier: 'open-decision',
        gate: 'admin',
        default: 'Dispatch and admin',
    },
    {
        id: 'r.consent',
        section: 'retention',
        kind: 'select',
        options: ['Every time', 'Once per ticket'],
        label: 'Diagnostics consent prompt',
        hint: 'Nothing leaves the machine without an explicit tap. Only the frequency is configurable.',
        tier: 'open-decision',
        gate: 'admin',
        default: 'Every time',
    },
    // section: ai — AI assistance (RUNTIME-HOT, gate: admin)
    {
        id: 'a.enabled',
        section: 'ai',
        kind: 'toggle',
        label: 'AI suggestions in the context pane',
        hint: 'Sends the conversation transcript to Anthropic to draft guidance for the technician. Suggestions are read-only — nothing is sent to the customer and no ticket is touched.',
        tier: 'runtime-hot',
        gate: 'admin',
        default: true,
    },
    {
        id: 'a.model',
        section: 'ai',
        kind: 'text',
        label: 'Model',
        hint: 'Anthropic model id. claude-sonnet-5 is the default; claude-opus-5 is slower and dearer for a side pane.',
        tier: 'runtime-hot',
        gate: 'admin',
        default: 'claude-sonnet-5',
    },
    {
        id: 'a.labelTranscripts',
        section: 'ai',
        kind: 'toggle',
        label: 'Speaker labels on call transcripts',
        hint: 'One small-model pass per call turns the raw transcript into Tech:/Caller: turns. Sends the transcript to Anthropic — this toggle is that consent, independent of the AI-suggestions switch above; the raw text stays the stored record either way.',
        tier: 'runtime-hot',
        gate: 'admin',
        default: true,
    },
    {
        id: 'a.labelModel',
        section: 'ai',
        kind: 'text',
        label: 'Transcript labeling model',
        hint: 'Deliberately separate from the context-pane model: labeling wants the cheapest capable tier. Anthropic model id.',
        tier: 'runtime-hot',
        gate: 'admin',
        default: 'claude-haiku-4-5-20251001',
    },
    {
        id: 'a.labelTimeoutS',
        section: 'ai',
        kind: 'number',
        unit: 's',
        label: 'Transcript labeling timeout',
        hint: 'How long a call journal waits for the labeling model before shipping the raw transcript instead. Longer calls need more: roughly 2s per transcript minute, so 60s covers most calls ever. A timeout never loses the transcript — it arrives unlabeled.',
        tier: 'runtime-hot',
        gate: 'admin',
        default: 60,
    },
];
export const configDefault = (id) => CONFIG_FIELDS.find((f) => f.id === id)?.default;
